import React, { useState, useEffect } from 'react';
import { ExamLibraryItem, TopicAnalysisItem } from '../types';
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from './icons';

interface VirtualOpticalFormProps {
    subjects: { key: string; max: number; qNums?: number[] }[];
    libraryExam?: ExamLibraryItem | null;
    mode?: 'solving' | 'creating';
    onUpdate: (scores: Record<string, { correct: number; incorrect: number; empty: number; net: number }>, topicAnalysis: TopicAnalysisItem[], rawAnswers: any[]) => void;
    onOptionSelect?: (subject: string, questionNumber: number, option: string) => void;
    onTopicSelect?: (subject: string, questionNumber: number) => void;
    answerKeyQuestions?: Record<string, { topic: string, answer: string }>;
    adminExam?: any;
    bookletType?: 'A' | 'B';
    initialSelectedOptions?: Record<string, string>;
    examType: 'TYT' | 'AYT' | 'YDT' | 'LGS';
}

type QuestionStatus = 'correct' | 'incorrect' | 'empty';

export const VirtualOpticalForm: React.FC<VirtualOpticalFormProps> = ({ subjects, libraryExam, mode = 'solving', onUpdate, onOptionSelect, onTopicSelect, answerKeyQuestions, adminExam, bookletType = 'A', initialSelectedOptions, examType }) => {
    const [activeSubject, setActiveSubject] = useState(subjects[0]?.key || '');
    // Store status for each question: { "Türkçe-1": "correct", "Türkçe-2": "incorrect" ... }
    const [answers, setAnswers] = useState<Record<string, QuestionStatus>>({});
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const OPTIONS = ['A', 'B', 'C', 'D', 'E'];

    // Initialize answers if empty or when initialSelectedOptions change
    useEffect(() => {
        let hasChanges = false;
        const newSelected = { ...selectedOptions, ...(initialSelectedOptions || {}) };
        const newAnswers: Record<string, QuestionStatus> = { ...answers };

        subjects.forEach(sub => {
            for (let i = 1; i <= sub.max; i++) {
                const qNum = sub.qNums ? sub.qNums[i - 1] : i;
                const key = `${sub.key}-${qNum}`;
                const marked = newSelected[key];
                
                if (marked) {
                    if (adminExam) {
                        const qInfo = adminExam.questions.find((q: any) => q.subject === sub.key && q.questionNumber === qNum);
                        const correct = bookletType === 'A' ? qInfo?.correctAnswerA : qInfo?.correctAnswerB;
                        newAnswers[key] = (marked === correct) ? 'correct' : 'incorrect';
                    } else if (libraryExam) {
                        const qInfo = libraryExam.questions.find((q: any) => q.subject === sub.key && q.questionNumber === qNum);
                        newAnswers[key] = (marked === qInfo?.correctAnswer) ? 'correct' : 'incorrect';
                    } else {
                        newAnswers[key] = 'correct'; // Default for manual entry if no key
                    }
                } else if (!newAnswers[key]) {
                    newAnswers[key] = 'empty';
                }
            }
        });

        setAnswers(newAnswers);
        setSelectedOptions(newSelected);
        calculateAndEmit(newAnswers, newSelected);
    }, [subjects, initialSelectedOptions]);

    // Helper to calculate scores
    const calculateAndEmit = (currentAnswers: Record<string, QuestionStatus>, currentSelected: Record<string, string>) => {
        const newScores: Record<string, { correct: number; incorrect: number; empty: number; net: number }> = {};
        const topicStats: Record<string, TopicAnalysisItem> = {};
        const rawAnswers: any[] = [];

        subjects.forEach(sub => {
            let correct = 0;
            let incorrect = 0;
            let empty = 0;

            for (let i = 1; i <= sub.max; i++) {
                const qNum = sub.qNums ? sub.qNums[i - 1] : i;
                const key = `${sub.key}-${qNum}`;
                const status = currentAnswers[key] || 'empty';
                const markedOption = currentSelected[key] || '';

                if (status === 'correct') correct++;
                else if (status === 'incorrect') incorrect++;
                else empty++;

                rawAnswers.push({
                    questionNumber: qNum,
                    subject: sub.key,
                    markedOption
                });

                // Topic Analysis Logic (Library Exam)
                if (libraryExam && libraryExam.questions) {
                    const qData = libraryExam.questions.find(q => q.subject === sub.key && q.questionNumber === qNum);
                    if (qData) {
                        const topicKey = `${sub.key}-${qData.topic}`;
                        if (!topicStats[topicKey]) {
                            topicStats[topicKey] = {
                                subject: sub.key,
                                topic: qData.topic,
                                correct: 0,
                                incorrect: 0,
                                empty: 0,
                                net: 0
                            };
                        }

                        if (status === 'correct') topicStats[topicKey].correct++;
                        else if (status === 'incorrect') topicStats[topicKey].incorrect++;
                        else topicStats[topicKey].empty++;

                        const wrongImpact = examType === 'LGS' ? (1 / 3) : 0.25;
                        topicStats[topicKey].net = topicStats[topicKey].correct - (topicStats[topicKey].incorrect * wrongImpact);
                    }
                }

                // Topic Analysis Logic (Admin Exam)
                if (adminExam && adminExam.questions) {
                    const qData = adminExam.questions.find((q: any) => q.subject === sub.key && q.questionNumber === qNum);
                    if (qData) {
                        const topicKey = `${sub.key}-${qData.topic || 'Genel'}`;
                        if (!topicStats[topicKey]) {
                            topicStats[topicKey] = {
                                subject: sub.key,
                                topic: qData.topic || 'Genel',
                                correct: 0,
                                incorrect: 0,
                                empty: 0,
                                net: 0
                            };
                        }

                        if (status === 'correct') topicStats[topicKey].correct++;
                        else if (status === 'incorrect') topicStats[topicKey].incorrect++;
                        else topicStats[topicKey].empty++;

                        const wrongImpact = examType === 'LGS' ? (1 / 3) : 0.25;
                        topicStats[topicKey].net = topicStats[topicKey].correct - (topicStats[topicKey].incorrect * wrongImpact);
                    }
                }
            }

                const wrongImpact = examType === 'LGS' ? (1 / 3) : 0.25;
                newScores[sub.key] = {
                    correct,
                    incorrect,
                    empty,
                    net: correct - (incorrect * wrongImpact)
                };
        });

        const topicAnalysis = Object.values(topicStats);
        onUpdate(newScores, topicAnalysis, rawAnswers);
    };

    const handleAnswerChange = (subject: string, questionNumber: number, status: QuestionStatus, currentSelected: Record<string, string>) => {
        const key = `${subject}-${questionNumber}`;
        const newAnswers = { ...answers, [key]: status };
        setAnswers(newAnswers);
        calculateAndEmit(newAnswers, currentSelected);
    };

    const handleOptionClick = (subject: string, questionNumber: number, option: string, correctAnswer?: string) => {
        const key = `${subject}-${questionNumber}`;

        if (mode === 'creating') {
            const newSelected = { ...selectedOptions, [key]: option };
            setSelectedOptions(newSelected);
            if (onOptionSelect) onOptionSelect(subject, questionNumber, option);
            return;
        }

        // Determine correct answer if adminExam is provided
        let effectiveCorrectAnswer = correctAnswer;
        if (adminExam && adminExam.questions) {
            const qData = adminExam.questions.find((q: any) => q.subject === subject && q.questionNumber === questionNumber);
            if (qData) {
                effectiveCorrectAnswer = bookletType === 'A' ? qData.correctAnswerA : qData.correctAnswerB;
            }
        }

        // Solving mode logic
        if (selectedOptions[key] === option) {
            const newSelected = { ...selectedOptions };
            delete newSelected[key];
            setSelectedOptions(newSelected);
            handleAnswerChange(subject, questionNumber, 'empty', newSelected);
            return;
        }

        const newSelected = { ...selectedOptions, [key]: option };
        setSelectedOptions(newSelected);

        if (effectiveCorrectAnswer) {
            const isCorrect = option.toUpperCase() === effectiveCorrectAnswer.toUpperCase();
            handleAnswerChange(subject, questionNumber, isCorrect ? 'correct' : 'incorrect', newSelected);
        } else {
            handleAnswerChange(subject, questionNumber, 'empty', newSelected);
        }
    };

    const handleBulkAction = (subject: string, action: 'all_correct' | 'all_empty') => {
        const sub = subjects.find(s => s.key === subject);
        if (!sub) return;

        const newAnswers = { ...answers };
        const newSelected = { ...selectedOptions };

        for (let i = 1; i <= sub.max; i++) {
            const qNum = sub.qNums ? sub.qNums[i - 1] : i;
            const key = `${subject}-${qNum}`;
            if (action === 'all_correct') {
                newAnswers[key] = 'correct';
                // Try to find correct option
                if (libraryExam && libraryExam.questions) {
                    const qData = libraryExam.questions.find(q => q.subject === subject && q.questionNumber === qNum);
                    if (qData && qData.correctAnswer) newSelected[key] = qData.correctAnswer;
                } else if (adminExam && adminExam.questions) {
                    const qData = adminExam.questions.find((q: any) => q.subject === subject && q.questionNumber === qNum);
                    if (qData) {
                        const ans = bookletType === 'A' ? qData.correctAnswerA : qData.correctAnswerB;
                        if (ans) newSelected[key] = ans;
                    }
                }
            } else {
                newAnswers[key] = 'empty';
                delete newSelected[key];
            }
        }
        setAnswers(newAnswers);
        setSelectedOptions(newSelected);
        calculateAndEmit(newAnswers, newSelected);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Subject Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-4 border-b mb-4 custom-scrollbar">
                {subjects.map(sub => (
                    <button
                        key={sub.key}
                        onClick={() => setActiveSubject(sub.key)}
                        className={`px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${activeSubject === sub.key
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        title={`${sub.key} Testine Geç`}
                    >
                        {sub.key}
                    </button>
                ))}
            </div>

            {/* Questions Grid - Realistic Optical Form Style */}
            <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar bg-gray-50/50 p-6 rounded-3xl border border-gray-100 dark:border-white/5">
                {subjects.map(sub => (
                    <div key={sub.key} className={activeSubject === sub.key ? 'block' : 'hidden'}>
                        {/* Realistic Header Info */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-gray-100 dark:border-white/10 shadow-sm">
                            <div>
                                <h4 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{sub.key} Testi</h4>
                                <p className="text-xs text-gray-400 font-bold mt-1 uppercase tracking-widest">{sub.max} Soru • Optik Kodlama</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleBulkAction(sub.key, 'all_correct')}
                                    className="text-[10px] font-black uppercase tracking-wider text-green-600 bg-green-50 dark:bg-green-500/10 px-4 py-2 rounded-xl hover:bg-green-100 transition-all active:scale-95 border border-green-100 dark:border-green-500/20"
                                    title="Tümünü Doğru İşaretle"
                                >
                                    Tümünü Doğru İşaretle
                                </button>
                                <button
                                    onClick={() => handleBulkAction(sub.key, 'all_empty')}
                                    className="text-[10px] font-black uppercase tracking-wider text-gray-500 bg-white dark:bg-slate-800 px-4 py-2 rounded-xl hover:bg-gray-100 transition-all active:scale-95 border border-gray-100 dark:border-white/10"
                                    title="Tüm Seçimleri Temizle"
                                >
                                    Temizle
                                </button>
                            </div>
                        </div>

                        {/* Optical Form Layout: Split into columns of 20 */}
                        <div className="flex flex-wrap gap-x-12 gap-y-8 justify-center lg:justify-start">
                            {(() => {
                                const columnsCount = Math.ceil(sub.max / 20);
                                return Array.from({ length: columnsCount }).map((_, colIdx) => (
                                    <div key={`col-${colIdx}`} className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm flex flex-col gap-1 w-[280px]">
                                        {/* Column Header */}
                                        <div className="grid grid-cols-6 mb-4 px-2">
                                            <div className="text-[10px] font-black text-gray-300 uppercase tracking-tighter">No</div>
                                            {OPTIONS.map(opt => (
                                                <div key={opt} className="text-[10px] font-black text-gray-300 text-center uppercase">{opt}</div>
                                            ))}
                                        </div>

                                        {/* Questions in this column */}
                                        {Array.from({ length: 20 }).map((_, qIdx) => {
                                            const relativeQNum = (colIdx * 20) + qIdx + 1;
                                            if (relativeQNum > sub.max) return null;

                                            const qNum = sub.qNums ? sub.qNums[relativeQNum - 1] : relativeQNum;
                                            const key = `${sub.key}-${qNum}`;
                                            const status = answers[key] || 'empty';
                                            const qData = libraryExam?.questions?.find(q => q.subject === sub.key && q.questionNumber === qNum);
                                            const adminQData = adminExam?.questions?.find((q: any) => q.subject === sub.key && q.questionNumber === qNum);
                                            const effectiveCorrectAnswer = adminQData ? (bookletType === 'A' ? adminQData.correctAnswerA : adminQData.correctAnswerB) : qData?.correctAnswer;
                                            const akData = answerKeyQuestions?.[key];

                                            // 5'li gruplama için boşluk veya çizgi
                                            const isGroupEnd = relativeQNum % 5 === 0 && relativeQNum % 20 !== 0;

                                            return (
                                                <React.Fragment key={key}>
                                                    <div className={`flex flex-col py-1 px-2 group hover:bg-indigo-50/30 dark:hover:bg-indigo-500/5 rounded-lg transition-colors ${status !== 'empty' ? 'bg-indigo-50/10' : ''}`}>
                                                        <div className="grid grid-cols-6 items-center">
                                                            {/* Question Number */}
                                                            <div className="text-xs font-black text-gray-400 dark:text-gray-500">{String(qNum).padStart(2, '0')}</div>

                                                            {/* Bubble Options */}
                                                            {OPTIONS.map(opt => {
                                                                const currentSelection = mode === 'creating' ? akData?.answer : selectedOptions[key];
                                                                const isSelected = currentSelection === opt;
                                                                const isCorrect = qData?.correctAnswer === opt;

                                                                let bubbleClass = "w-7 h-7 rounded-full border-2 text-[10px] font-black transition-all flex items-center justify-center relative ";

                                                                if (isSelected) {
                                                                    if (mode === 'creating') {
                                                                        bubbleClass += "bg-indigo-600 border-indigo-700 text-white shadow-lg";
                                                                    } else if (!qData) {
                                                                        // Simple selection mode (no key)
                                                                        bubbleClass += "bg-slate-700 border-slate-800 text-white shadow-inner";
                                                                    } else if (isCorrect) {
                                                                        bubbleClass += "bg-green-500 border-green-600 text-white shadow-lg";
                                                                    } else {
                                                                        bubbleClass += "bg-red-500 border-red-600 text-white shadow-lg";
                                                                    }
                                                                } else {
                                                                    if (mode !== 'creating' && status === 'incorrect' && isCorrect) {
                                                                        // Show correct answer if wrong
                                                                        bubbleClass += "bg-green-100 dark:bg-green-500/10 border-green-400 text-green-600";
                                                                    } else {
                                                                        bubbleClass += "bg-white dark:bg-slate-800 border-gray-200 dark:border-white/10 text-gray-300 group-hover:border-indigo-300";
                                                                    }
                                                                }

                                                                return (
                                                                    <div key={opt} className="flex justify-center">
                                                                        <button
                                                                            onClick={() => handleOptionClick(sub.key, qNum, opt, effectiveCorrectAnswer)}
                                                                            className={bubbleClass}
                                                                            title={mode === 'creating' ? `Cevap Şıkkı Olarak Belirle: ${opt}` : (effectiveCorrectAnswer ? (isSelected && opt === effectiveCorrectAnswer ? "Doğru Cevap" : "Yanlış Şık") : `İşaretle: ${opt}`)}
                                                                        >
                                                                            {opt}
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        {mode === 'creating' && (
                                                            <div className="flex justify-between items-center mt-1 pl-6">
                                                                <button
                                                                    onClick={() => onTopicSelect && onTopicSelect(sub.key, qNum)}
                                                                    className="text-[9px] font-black text-indigo-400 hover:text-indigo-600 uppercase tracking-tighter"
                                                                    title="Soruya Konu Ata"
                                                                >
                                                                    {akData?.topic ? `Konu: ${akData.topic}` : "Konu Seç +"}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isGroupEnd && <div className="h-px bg-gray-50 dark:bg-white/5 my-1 mx-2" />}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
